import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";

type Client = components["schemas"]["ClientResponse"];

interface EditClientDialogProps {
  client: Client;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: (updated: Client) => void;
}

export function EditClientDialog({ client, open, onOpenChange, onUpdated }: EditClientDialogProps) {
  const [name, setName] = useState(client.name);
  const [dolfinRevenueSharePercent, setDolfinRevenueSharePercent] = useState(
    String(client.dolfinRevenueSharePercent ?? ""),
  );
  const [paymentRevenueSharePercent, setPaymentRevenueSharePercent] = useState(
    String(client.paymentRevenueSharePercent ?? ""),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(client.name);
      setDolfinRevenueSharePercent(String(client.dolfinRevenueSharePercent ?? ""));
      setPaymentRevenueSharePercent(String(client.paymentRevenueSharePercent ?? ""));
      setError(null);
    }
  }, [open, client]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError("Client name is required.");
      return;
    }
    const dolfin = parseFloat(dolfinRevenueSharePercent);
    const clientShare = parseFloat(paymentRevenueSharePercent);
    if (Number.isNaN(dolfin) || dolfin < 0) {
      setError("Dolfin revenue share must be a non-negative number.");
      return;
    }
    if (Number.isNaN(clientShare) || clientShare < 0) {
      setError("Client revenue share must be a non-negative number.");
      return;
    }

    if (!client.id) return;

    setSubmitting(true);
    const { data, error: apiError } = await api.PUT("/clients/{id}", {
      params: { path: { id: client.id } },
      body: {
        name: name.trim(),
        dolfinRevenueSharePercent: dolfin,
        paymentRevenueSharePercent: clientShare,
      },
    });

    if (apiError || !data) {
      setError("Failed to update client.");
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    onOpenChange(false);
    onUpdated(data);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Client</DialogTitle>
            <DialogDescription>
              Rate changes apply to new PaymentIntents only — historical payments keep the rates
              they were charged at.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-client-name">Name</Label>
              <Input
                id="edit-client-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-client-dolfin-rev">Dolfin revenue share (%)</Label>
                <Input
                  id="edit-client-dolfin-rev"
                  type="number"
                  step="0.01"
                  min="0"
                  value={dolfinRevenueSharePercent}
                  onChange={(e) => setDolfinRevenueSharePercent(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-client-client-rev">Client revenue share (%)</Label>
                <Input
                  id="edit-client-client-rev"
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentRevenueSharePercent}
                  onChange={(e) => setPaymentRevenueSharePercent(e.target.value)}
                />
              </div>
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
